import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, X, Search, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { SEO } from '../components/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLanguageStore, useWishlistStore, useAuthStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';
import { ProductFilters } from '../components/catalog/ProductFilters';
import { ProductGrid, CollectionGroupedGrid } from '../components/catalog/ProductGrid';

const API = process.env.REACT_APP_BACKEND_URL;

const STATIC_CATEGORIES = [
  { id: "kitchens", name: { en: "Kitchens", nl: "Keukens", ro: "Bucătării", ru: "Кухни" } },
  { id: "wardrobes", name: { en: "Wardrobes", nl: "Kasten / Garderobes", ro: "Garderobe", ru: "Шкафы" } },
  { id: "doors", name: { en: "Doors", nl: "Deuren", ro: "Uși", ru: "Двери" } },
  { id: "wall_panels", name: { en: "Wall Panels", nl: "Wandpanelen", ro: "Panouri de Perete", ru: "Стеновые панели" } },
  { id: "soft_furniture", name: { en: "Soft Furniture", nl: "Zachte Meubels", ro: "Mobilier Tapițat", ru: "Мягкая мебель" } },
  { id: "ready_solutions", name: { en: "Ready Solutions", nl: "Kant-en-klare Oplossingen", ro: "Soluții Gata", ru: "Готовые решения" } },
  { id: "custom_furniture", name: { en: "Custom Furniture", nl: "Maatwerk Meubels", ro: "Mobilier la Comandă", ru: "Мебель на заказ" } }
];

const STATIC_DOOR_SUBCATEGORIES = [
  { id: "invisible_doors", name: { en: "Invisible Doors", nl: "Onzichtbare Deuren", ro: "Uși filomuro", ru: "Скрытые Двери" } },
  { id: "sliding_partitions", name: { en: "Sliding Partitions", nl: "Schuifwanden", ro: "Partiții glisante", ru: "Раздвижные перегородки" } },
  { id: "pivot_doors", name: { en: "Pivot Doors", nl: "Pivotdeuren", ro: "Uși pivotante", ru: "Поворотные двери" } }
];

// Глобальный кэш оперативной памяти для предотвращения сброса стейта при смене роутов/языков
let globalCategories = STATIC_CATEGORIES;
let globalDoorSubcategories = STATIC_DOOR_SUBCATEGORIES;
let globalProductsCache = {};

const getRussianPluralProducts = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 19) return 'товаров';
  if (mod10 === 1) return 'товар';
  if (mod10 >= 2 && mod10 <= 4) return 'товара';
  return 'товаров';
};

export const ProductsPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { isAuthenticated } = useAuthStore();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlistStore();
  
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Синхронный сбор параметров из нативного URL для нулевой задержки на первом кадре рендеринга
  const syncParams = useMemo(() => {
    const query = new URLSearchParams(window.location.search);
    return {
      category: query.get('category') || '',
      subcategory: query.get('subcategory') || '',
      materials: (query.get('materials') || '').split(',').filter(Boolean),
      finishes: (query.get('finishes') || '').split(',').filter(Boolean),
      q: query.get('q') || '',
      sort: query.get('sort') || 'featured'
    };
  }, [location.search]);

  // Filters State (локальные стейты обеспечивают мгновенный отклик UI)
  const [selectedCategory, setSelectedCategory] = useState(syncParams.category);
  const [selectedSubcategory, setSelectedSubcategory] = useState(syncParams.subcategory);
  const [selectedMaterials, setSelectedMaterials] = useState(syncParams.materials);
  const [selectedFinishes, setSelectedFinishes] = useState(syncParams.finishes);
  const [searchQuery, setSearchQuery] = useState(syncParams.q);
  const [sortBy, setSortBy] = useState(syncParams.sort);

  // Синхронизируем стейт при прямом изменении URL (например, при переходе из меню шапки)
  useEffect(() => {
    setSelectedCategory(syncParams.category);
    setSelectedSubcategory(syncParams.subcategory);
    setSelectedMaterials(syncParams.materials);
    setSelectedFinishes(syncParams.finishes);
    setSearchQuery(syncParams.q);
    setSortBy(syncParams.sort);
  }, [syncParams]);

  // Стейт поисковой строки (для плавности ввода)
  const [searchInput, setSearchInput] = useState(searchQuery);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Дебаунс поискового запроса
  useEffect(() => {
    const delayDebounceId = setTimeout(() => {
      if (searchInput.trim() !== searchQuery.trim()) {
        const nextParams = new URLSearchParams(window.location.search);
        if (searchInput.trim()) {
          nextParams.set('q', searchInput.trim());
        } else {
          nextParams.delete('q');
        }
        setSearchParams(nextParams);
      }
    }, 350);
    return () => clearTimeout(delayDebounceId);
  }, [searchInput]);

  const [categories, setCategories] = useState(globalCategories);
  const [doorSubcategories, setDoorSubcategories] = useState(globalDoorSubcategories);
  const [filterOpen, setFilterOpen] = useState(false);

  // Уникальный ключ кэша на основе комбинации выбранных фильтров
  const cacheKey = useMemo(() => {
    return [
      selectedCategory,
      selectedSubcategory,
      selectedMaterials.join(','),
      selectedFinishes.join(','),
      searchQuery.trim()
    ].join('|');
  }, [selectedCategory, selectedSubcategory, selectedMaterials, selectedFinishes, searchQuery]);

  const [products, setProducts] = useState(globalProductsCache[cacheKey] || []);
  const [loading, setLoading] = useState(!globalProductsCache[cacheKey]);
  const [isRefreshing, setIsRefreshing] = useState(false); 

  // Динамическая карта категорий, собранная из MongoDB контент-хаба
  const dynamicCategoryLabels = useMemo(() => {
    return categories.reduce((acc, cat) => {
      acc[cat.id] = getLocalized(cat.name, language);
      return acc;
    }, {});
  }, [categories, language]);

  // Активный заголовок текущей выбранной категории из базы данных
  const activeCategoryTitle = useMemo(() => {
    if (!selectedCategory) return t.lookbook?.title || 'Lookbook';
    const match = categories.find(c => c.id === selectedCategory);
    return match ? getLocalized(match.name, language) : 'Lookbook';
  }, [selectedCategory, categories, language, t.lookbook?.title]);

  // Получение категорий и подкатегорий из БД с бесшовной записью в кэш
  useEffect(() => {
    fetch(`${API}/api/products/categories/list?t=${Date.now()}`)
      .then((res) => res.json())
      .then((data) => {
        const cats = data.categories || [];
        const subcats = data.door_subcategories || [];
        globalCategories = cats;
        globalDoorSubcategories = subcats;
        setCategories(cats);
        setDoorSubcategories(subcats);
      })
      .catch(console.error);
  }, []);

  // Запрос к API выполняется строго при изменении параметров фильтрации и поиска!
  useEffect(() => {
    let active = true;

    // Если у нас есть кэш для текущей комбинации фильтров, мгновенно выводим его без лоадера
    if (globalProductsCache[cacheKey]) {
      setProducts(globalProductsCache[cacheKey]);
      setLoading(false);
      setIsRefreshing(true); // Показываем тихий фоновый индикатор обновления
    } else {
      setLoading(true);
      setIsRefreshing(false);
    }

    const fetchProducts = async () => {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedSubcategory) params.append('subcategory', selectedSubcategory);
      if (selectedMaterials.length) params.append('materials', selectedMaterials.join(','));
      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      if (selectedFinishes.length) params.append('finishes', selectedFinishes.join(','));

      try {
        const res = await fetch(`${API}/api/products?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to fetch products');
        const data = await res.json();
        
        if (active) {
          globalProductsCache[cacheKey] = data;
          setProducts(data);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        if (active) {
          setLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    fetchProducts();
    return () => {
      active = false;
    };
  }, [cacheKey]);

  // Мягкая сортировка продуктов на клиенте (выполняется мгновенно без моргания)
  const processedProducts = useMemo(() => {
    const list = [...products];
    if (sortBy === 'name') {
      list.sort((a, b) => {
        const nameA = getLocalized(a.name, language) || '';
        const nameB = getLocalized(b.name, language) || '';
        return nameA.localeCompare(nameB, language);
      });
    }
    return list;
  }, [products, sortBy, language]);

  const handleWishlist = async (e, productId) => {
    e.preventDefault();
    if (!isAuthenticated) return;
    if (isInWishlist(productId)) await removeFromWishlist(productId);
    else await addToWishlist(productId);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedMaterials([]);
    setSelectedFinishes([]);
    setSearchQuery('');
    setSearchParams({});
    setSearchInput('');
  };

  // ИСПРАВЛЕНО: Добавлены мгновенные обновления локального стейта (0мс задержки) для чекбоксов
  const handleCategoryChange = (id) => {
    setSelectedCategory(id);
    setSelectedSubcategory('');
    const nextParams = new URLSearchParams(window.location.search);
    if (id) {
      nextParams.set('category', id);
    } else {
      nextParams.delete('category');
    }
    nextParams.delete('subcategory');
    setSearchParams(nextParams);
  };

  const handleSubcategoryChange = (id) => {
    setSelectedSubcategory(id);
    const nextParams = new URLSearchParams(window.location.search);
    if (id) {
      nextParams.set('category', 'doors');
      nextParams.set('subcategory', id);
    } else {
      nextParams.delete('subcategory');
    }
    setSearchParams(nextParams);
  };

  const handleMaterialsChange = (updater) => {
    const next = typeof updater === 'function' ? updater(selectedMaterials) : updater;
    setSelectedMaterials(next);
    const nextParams = new URLSearchParams(window.location.search);
    if (next.length) {
      nextParams.set('materials', next.join(','));
    } else {
      nextParams.delete('materials');
    }
    setSearchParams(nextParams);
  };

  const handleFinishesChange = (updater) => {
    const next = typeof updater === 'function' ? updater(selectedFinishes) : updater;
    setSelectedFinishes(next);
    const nextParams = new URLSearchParams(window.location.search);
    if (next.length) {
      nextParams.set('finishes', next.join(','));
    } else {
      nextParams.delete('finishes');
    }
    setSearchParams(nextParams);
  };

  const handleSortChange = (val) => {
    setSortBy(val);
    const nextParams = new URLSearchParams(window.location.search);
    if (val && val !== 'featured') {
      nextParams.set('sort', val);
    } else {
      nextParams.delete('sort');
    }
    setSearchParams(nextParams);
  };

  const hasActiveFilters = Boolean(
    selectedCategory || selectedSubcategory || selectedMaterials.length || selectedFinishes.length || searchQuery
  );

  const filtersLabels = {
    category: t.common.category,
    subcategory: t.common.subcategory || 'Subcategory',
    material: t.common.material,
    finish: t.common.finish,
    clearAll: t.common?.clearFilters || 'Clear All Filters',
  };

  // Мягкая группировка продуктов по категориям в памяти
  const groups = useMemo(() => {
    return processedProducts.reduce((acc, product) => {
      const key = product.category || 'other';
      if (!acc[key]) acc[key] = [];
      acc[key].push(product);
      return acc;
    }, {});
  }, [processedProducts]);

  const orderedKeys = useMemo(() => {
    const keys = categories.map((c) => c.id).filter((id) => groups[id]?.length > 0);
    Object.keys(groups).forEach((k) => { 
      if (!keys.includes(k)) keys.push(k); 
    });
    return keys;
  }, [categories, groups]);

  const filtersNode = (
    <ProductFilters
      categories={categories}
      doorSubcategories={doorSubcategories}
      selectedCategory={selectedCategory}
      selectedSubcategory={selectedSubcategory}
      selectedMaterials={selectedMaterials}
      selectedFinishes={selectedFinishes}
      language={language}
      labels={filtersLabels}
      hasActiveFilters={hasActiveFilters}
      onCategoryChange={handleCategoryChange}
      onSubcategoryChange={handleSubcategoryChange}
      onMaterialsChange={handleMaterialsChange}
      onFinishesChange={handleFinishesChange}
      onClear={clearFilters}
    />
  );

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      <SEO
        title={`${activeCategoryTitle} — FEDYK Furniture`}
        description={`Browse premium ${selectedCategory || 'furniture'} collections by FEDYK Furniture`}
        path="products"
      />
      
      <div className="bg-background section-padding">
        <div className="container-custom">
          {/* ИСПРАВЛЕНО: Ускорена Framer-Motion анимация наведения и появления страницы с 0.6s до 0.3s */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <p className="text-caption text-muted-foreground mb-6">{t.lookbook?.eyebrow || 'Collections'}</p>
            <div className="flex items-center gap-3 mb-4">
              <h1 className="collection-title text-h1 font-extralight text-gray-900 m-0">
                {activeCategoryTitle}
              </h1>
              {isRefreshing && (
                <Loader2 className="w-5 h-5 animate-spin text-neutral-400 self-end mb-2" />
              )}
            </div>
            <p className="text-body-lg max-w-xl mb-10 font-light text-neutral-600 leading-relaxed">
              {t.lookbook?.description || 'A curated selection of bespoke pieces. Each design begins with a conversation — request a concept tailored to your space.'}
            </p>
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t.common?.search || 'Search products...'}
                className="w-full pl-11 pr-4 py-3 bg-background border border-border text-sm focus:outline-none focus:border-foreground/40 transition-colors rounded-none text-gray-900"
                data-testid="product-search-input"
              />
              {searchInput && (
                <button onClick={() => setSearchInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container-custom py-12 text-left">
        <div className="flex gap-12">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0 text-left">
            <div className="sticky top-28">
              <h3 className="text-lg font-medium mb-8 text-gray-900">{t.common.filter}</h3>
              {filtersNode}
            </div>
          </aside>

          {/* Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
              <p className="text-sm text-muted-foreground font-light">
                {products.length} {
                  language === 'ru' ? getRussianPluralProducts(products.length) : 
                  language === 'nl' ? (products.length === 1 ? 'product' : 'producten') : 
                  language === 'ro' ? (products.length === 1 ? 'produs' : 'produse') : 
                  (products.length === 1 ? 'product' : 'products')
                }
              </p>

              <div className="flex items-center gap-4">
                <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden rounded-none h-10 px-4" data-testid="mobile-filter-btn">
                      <Filter className="w-4 h-4 mr-2" />
                      {t.common.filter}
                      {hasActiveFilters && (
                        <span className="ml-2 w-5 h-5 bg-foreground text-background text-xs flex items-center justify-center">!</span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 overflow-y-auto rounded-none">
                    <SheetHeader>
                      <SheetTitle>{t.common.filter}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-8">{filtersNode}</div>
                  </SheetContent>
                </Sheet>

                <Select value={sortBy} onValueChange={handleSortChange}>
                  <SelectTrigger className="w-44 rounded-none h-10 border-gray-200 bg-white text-gray-900 text-xs tracking-wider" data-testid="sort-select">
                    <SelectValue placeholder={t.common.sortBy} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="featured" className="rounded-none text-xs">{t.common?.featured || 'Featured'}</SelectItem>
                    <SelectItem value="name" className="rounded-none text-xs">{t.common?.name || 'Name'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading && products.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-20 md:gap-x-16">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/5] bg-muted mb-6" />
                    <div className="h-4 bg-muted w-3/4 mb-2" />
                    <div className="h-4 bg-muted w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-gray-200 bg-gray-50/50">
                <p className="text-muted-foreground text-sm font-light mb-4">{t.lookbook?.empty || 'No pieces match your filters.'}</p>
                <Button variant="outline" className="rounded-none text-xs px-5" onClick={clearFilters}>
                  {t.lookbook?.reset || 'Reset Filters'}
                </Button>
              </div>
            ) : selectedCategory ? (
              <ProductGrid
                products={processedProducts}
                language={language}
                categoryLabels={dynamicCategoryLabels}
                ctaLabel={t.lookbook?.requestConcept || 'Request Concept'}
                isAuthenticated={isAuthenticated}
                isInWishlist={isInWishlist}
                onWishlistToggle={handleWishlist}
              />
            ) : (
              <CollectionGroupedGrid
                groups={groups}
                orderedKeys={orderedKeys}
                categoryLabels={dynamicCategoryLabels}
                eyebrowLabel={t.lookbook?.eyebrow || 'Collection'}
                viewAllLabel={t.common.viewAll || 'View All'}
                language={language}
                ctaLabel={t.lookbook?.requestConcept || 'Request Concept'}
                isAuthenticated={isAuthenticated}
                isInWishlist={isInWishlist}
                onWishlistToggle={handleWishlist}
                onViewCategory={(catId) => {
                  handleCategoryChange(catId);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
